import React, { useState } from 'react';

import {
  Grid,
  TextField,
  Button,
  Typography,
  Select,
  MenuItem,
  Box,
  SelectChangeEvent,
  FormControl,
  InputLabel,
  Container,
  useTheme,
} from '@mui/material';

import RestUtilities from '../services/RestUtilities';

import { useSetRecoilState } from 'recoil';
import { loadingState, toastState } from '../state/RecoilState';
import validator from 'validator';
import MfsRichTextEditor from '../components/Common/RichText/RichTextEditor';

interface Faq {
  question: string;
  answer: string;
  editor: any;
  id: number;
  cmsContentId: number;
}
interface FormState {
  currentId: number;
  pageTitle: string;
  pageHeading: string;
  metaDescription: string;
  pageContent?: string;
  faqTitle: string;
  cmsContentFaqs: Array<Faq>;
}
const initialState: FormState = {
  currentId: 0,
  pageTitle: '',
  pageHeading: '',
  metaDescription: '',
  pageContent: '',
  faqTitle: '',
  cmsContentFaqs: [],
};

const StaticContent = () => {
  const theme = useTheme();
  const setToast = useSetRecoilState(toastState);
  const [formState, setFormState] = useState<FormState>({
    ...initialState,
    currentId: 0,
  });
  const setLoading = useSetRecoilState(loadingState);
  const [editor, setEditor] = useState<any>(undefined);
  const [isTouched, setIsTouched] = useState(false);

  const onUpdate = () => {
    setLoading(true);
    const {
      pageTitle,
      metaDescription,
      pageHeading,
      faqTitle,
      currentId,
      cmsContentFaqs,
    } = formState;
    const content = editor?.getHTML();
    const name =
      pages.find((x) => x.id === Number(currentId))?.text ?? 'Name Unknown';
    cmsContentFaqs.forEach((f: Faq) => {
      f.answer = f.editor?.getHTML();
      f.editor = undefined;
    });
    const data = {
      id: currentId,
      title: pageTitle,
      metaDescription,
      heading: pageHeading,
      content,
      name,
      faqTitle,
      cmsContentFaqs: cmsContentFaqs,
    };
    const url = `api/StaticContent/${currentId}`;
    RestUtilities.put(url, data);
    setToast('Saved');
    setFormState({ ...formState });
    setLoading(false);
    setIsTouched(false);
  };

  const loadPage = async (value: number | string) => {
    setLoading(true);
    const url = `api/StaticContent/${value}`;
    const response = await RestUtilities.get(url);
    if (response.ok) {
      const page = response.content;
      setFormState({
        ...formState,
        metaDescription: page.metaDescription,
        pageTitle: page.title,
        pageHeading: page.heading ?? '',
        pageContent: page.content,
        faqTitle: page.faqTitle ?? '',
        currentId: Number(value),
        cmsContentFaqs: page.cmsContentFaqs ?? [],
      });
      setToast('Loaded');
    }
    setIsTouched(false);
    setLoading(false);
  };
  const handlePageSelect = (e: SelectChangeEvent) => {
    loadPage(e.target.value);
  };

  const handlePageTitleChange = (e: any) => {
    const pageTitle = validator.escape(e.currentTarget.value);
    setFormState({
      ...formState,
      pageTitle,
    });
    setIsTouched(true);
  };

  const handleFaqTitleChange = (e: any) => {
    const title = validator.escape(e.currentTarget.value);
    setFormState({
      ...formState,
      faqTitle: title,
    });
    setIsTouched(true);
  };

  const handlePageHeadingChange = (e: any) => {
    const pageHeading = validator.escape(e.currentTarget.value);
    setFormState({
      ...formState,
      pageHeading,
    });
    setIsTouched(true);
  };

  const handleMetaDescriptionChange = (e: any) => {
    const metaDescription = validator.escape(e.currentTarget.value);
    setFormState({
      ...formState,
      metaDescription,
    });
    setIsTouched(true);
  };

  const handleFaqQuestionChange = (e: any, faq: Faq) => {
    let updatedValue = e.currentTarget.value;
    const faqToUpdate = formState.cmsContentFaqs.find((x: Faq) => x === faq);
    if (faqToUpdate) {
      faqToUpdate.question = updatedValue;
    }
    setFormState({
      ...formState,
      cmsContentFaqs: [...formState.cmsContentFaqs],
    });
  };

  const onAddNewFaq = () => {
    setIsTouched(true);
    const newFaq = {
      id: -1,
      cmsContentId: formState.currentId,
      question: '',
      answer: '',
      editor: undefined,
    };
    setFormState({
      ...formState,
      cmsContentFaqs: [...formState.cmsContentFaqs, newFaq],
    });
  };

  const onRemoveFaq = (faq: Faq) => {
    setIsTouched(true);
    setFormState({
      ...formState,
      cmsContentFaqs: [
        ...formState.cmsContentFaqs.filter((x: Faq) => x !== faq),
      ],
    });
  };

  const setFaqEditor = (editor: any, faq: Faq) => {
    const faqToUpdate = formState.cmsContentFaqs.find((x: Faq) => x === faq);
    if (faqToUpdate) {
      faqToUpdate.editor = editor;
    }
  };

  return (
    <Container maxWidth='lg'>
      <Typography variant='h4' gutterBottom>
        Music for Scotland Website
      </Typography>
      <Grid container>
        <Grid item xs={12}>
          <Box display='flex' justifyContent={'flex-start'} sx={{ my: 1 }}>
            <FormControl sx={{ minWidth: 300 }} size='small'>
              <InputLabel id='demo-select-small-label'>Select Page</InputLabel>
              <Select
                fullWidth
                value={formState.currentId ? String(formState.currentId) : ''}
                onChange={handlePageSelect}
                className='select-row'
                placeholder='Select Page'
                label='Select Page'
              >
                {pages
                  .sort((a: any, b: any) =>
                    a.text < b.text ? -1 : a.text > b.text ? 1 : 0
                  )
                  .map((x) => (
                    <MenuItem key={x.id} value={String(x.id)}>
                      {x.text}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            {formState.currentId !== 0 && (
              <>
                <Button
                  variant='contained'
                  color='primary'
                  onClick={onUpdate}
                  sx={{ ml: 1 }}
                  size='small'
                  disabled={!isTouched}
                >
                  Save Changes
                </Button>
              </>
            )}
          </Box>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            size='small'
            label='Page Title'
            placeholder='Page Title'
            value={formState.pageTitle ?? ''}
            onChange={handlePageTitleChange}
            sx={{ my: 1 }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            multiline
            id='clientComment'
            label='MetaDescription'
            value={formState.metaDescription ?? ''}
            fullWidth
            margin='normal'
            onChange={handleMetaDescriptionChange}
            InputProps={{
              rows: 2,
              multiline: true,
              inputComponent: 'textarea',
            }}
            variant='outlined'
            sx={{ my: 1 }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            placeholder='This should be the only <h1> on the page'
            value={formState.pageHeading ?? ''}
            onChange={handlePageHeadingChange}
            fullWidth
            size='small'
            label='H1 Title'
            sx={{ my: 1 }}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant='subtitle1' color={theme.palette.text.secondary}>
            Main Content
          </Typography>
          <Typography variant='body2' gutterBottom>
            Please do not use Heading 1 (&lt;h1&gt;) in the content below. It is
            explicitly defined above (there should only be one on the page)!
          </Typography>
          {formState.pageContent && (
            <Box sx={{ my: 1, pb: 3 }}>
              <MfsRichTextEditor
                setEditor={(editor) => setEditor(editor)}
                content={formState.pageContent ?? ''}
                setTouched={() => setIsTouched(true)}
                isSatellite={false}
              />
            </Box>
          )}
        </Grid>
        <Grid item xs={12}>
          <Typography variant='subtitle1' color={theme.palette.text.secondary}>
            Google Structured Faqs Markup
          </Typography>
          <Typography variant='body2' gutterBottom>
            To add FAQs to google structured mark please add them below. Plain
            text only, no links or html. After making changes, remember to click
            save changes at the top.
          </Typography>
          <TextField
            fullWidth
            size='small'
            label='Optional Faq Heading Text'
            placeholder='Frequently asked questions about ....'
            value={formState.faqTitle ?? ''}
            onChange={handleFaqTitleChange}
            sx={{ my: 3 }}
          />
          <Button
            variant='contained'
            color='primary'
            onClick={onAddNewFaq}
            size='small'
          >
            Click to add new FAQ
          </Button>

          <Box sx={{ my: 2, pb: 3 }}>
            {formState.cmsContentFaqs.map((x, i) => (
              <Box mb={2} key={`${x.id}_${i}`}>
                <TextField
                  fullWidth
                  size='small'
                  label={`FAQ ${i + 1}`}
                  placeholder='Write the question'
                  value={x.question ?? ''}
                  onChange={(e) => handleFaqQuestionChange(e, x)}
                  sx={{ my: 1 }}
                />
                <Box sx={{ my: 1 }}>
                  <MfsRichTextEditor
                    setEditor={(editor) => setFaqEditor(editor, x)}
                    content={x.answer ?? ''}
                    setTouched={() => setIsTouched(true)}
                    isSatellite={false}
                  />
                </Box>
                <Button
                  variant='contained'
                  color='primary'
                  onClick={() => onRemoveFaq(x)}
                  size='small'
                >
                  Remove FAQ {i + 1}
                </Button>
              </Box>
            ))}
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default StaticContent;

const pages = [
  { id: 1, text: 'Home - Meta' },
  { id: 3, text: 'Home - Right Text' },
  { id: 4, text: 'Home - Left Text' },
  { id: 6, text: 'DJs' },
  { id: 7, text: 'Pipers' },
  { id: 8, text: 'Finnegans Wake' },
  { id: 9, text: 'Your Event' },
  { id: 10, text: 'Wedding Music' },
  { id: 12, text: 'Showcases' },
  { id: 13, text: 'Contact Us' },
  { id: 14, text: 'About Us' },
  { id: 15, text: 'Faqs' },
  { id: 16, text: 'Testimonials' },
  { id: 17, text: 'Corporate Events' },
  { id: 18, text: 'Privacy and Terms' },
  { id: 19, text: 'Wedding Bands - Edinburgh' },
  { id: 20, text: 'Wedding Bands - Glasgow' },
  { id: 21, text: 'Wedding Bands - Aberdeen' },
  { id: 22, text: 'Wedding Bands - Dundee' },
  { id: 23, text: 'Wedding Bands - Fife' },
  { id: 24, text: 'Wedding Bands - Scotland' },
  { id: 25, text: 'Wedding Bands - Central' },
  { id: 26, text: 'Ceilidh Covers Band - Edinburgh' },
  { id: 27, text: 'Ceilidh Covers Band - Glasgow' },
  { id: 28, text: 'Ceilidh Covers Band - Aberdeen' },
  { id: 29, text: 'Ceilidh Covers Band - Dundee' },
  { id: 30, text: 'Ceilidh Covers Band - Fife' },
  { id: 31, text: 'Wedding Band Scotland' },
  { id: 32, text: 'Best Glasgow Wedding Venues' },
  { id: 33, text: 'Pipers Glasgow' },
  { id: 34, text: 'Wedding Venues Scotland' },
  { id: 35, text: 'Guide to Wedding Band Prices' },
  { id: 37, text: 'Wedding Suppliers Scotland' },
  { id: 38, text: 'Covid Support' },
  { id: 39, text: 'Best Wedding Venues Edinburgh' },
  { id: 40, text: 'Best Wedding Suppliers Edinburgh' },
  { id: 41, text: 'Best Wedding Suppliers Glasgow' },
  { id: 42, text: 'Top Wedding Bands Aberdeen' },
  { id: 43, text: 'Top Wedding Bands Edinburgh' },
  { id: 44, text: 'Top Wedding Bands Glasgow' },
  { id: 45, text: 'Best Wedding Venues Glasgow' },
  { id: 46, text: 'Wedding Bands - Ayrshire' },
];
